import React, { useEffect, useState } from "react";
import { Table, Button, Tabs, Tab, Modal, Form, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { collection, getDocs, orderBy, query, limit, startAfter, Timestamp, doc, updateDoc, where, deleteDoc, getDoc, setDoc } from "firebase/firestore";
import Airtable from "airtable";

// Constants for Airtable
const AIRTABLE_API_KEY = process.env.REACT_APP_AIRTABLE_API_KEY;
const AIRTABLE_BASE_ID = "apprwDgpLMtBvjfTQ";
const AIRTABLE_TABLE_ID = "tblevlgLvZNOyVW4E"; // Priority table ID
const AIRTABLE_SESSION_FIELD = "session reference from db"; // Field name for session ID

// Vector icons from public folder
const imgSessionsIcon = "/icons/sessions-icon.svg";
const imgViewIcon = "/icons/view-icon.svg";
const imgMoveIcon = "/icons/move-icon.svg";
const imgMergeIcon = "/icons/merge-icon.svg";
const imgDeleteIcon = "/icons/delete-icon.svg";

// Custom styles to match Figma design
const customStyles = {
  container: {
    backgroundColor: '#f8f8f8',
    minHeight: '100vh',
    padding: '20px',
    fontFamily: 'Circular Std, -apple-system, BlinkMacSystemFont, sans-serif'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    marginBottom: '30px',
    marginLeft: '10px'
  },
  headerIcon: {
    width: '46.625px',
    height: '38px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  headerTitle: {
    fontSize: '30px',
    fontWeight: 'bold',
    color: '#101828',
    margin: 0,
    lineHeight: '38px'
  },
  tabsContainer: {
    marginBottom: '20px'
  },
  tabButton: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#bebebe',
    border: 'none',
    backgroundColor: 'transparent',
    padding: '10px 20px',
    cursor: 'pointer',
    transition: 'color 0.2s ease'
  },
  tabButtonActive: {
    color: '#101828'
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: '5px',
    border: '1px solid #bebebe',
    overflow: 'hidden'
  },
  tableHeader: {
    backgroundColor: 'transparent',
    borderBottom: '1px solid #bebebe'
  },
  tableHeaderCell: {
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#565656',
    padding: '15px 20px',
    border: 'none',
    backgroundColor: 'transparent'
  },
  tableBody: {
    backgroundColor: 'white'
  },
  tableRow: {
    borderBottom: '1px solid #bebebe'
  },
  tableCell: {
    fontSize: '12px',
    color: '#565656',
    padding: '20px',
    border: 'none',
    backgroundColor: 'transparent',
    verticalAlign: 'top'
  },
  sessionName: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#565656',
    marginBottom: '5px'
  },
  sessionDescription: {
    fontSize: '10px',
    color: '#9180fd',
    textDecoration: 'underline',
    cursor: 'pointer',
    marginTop: '5px'
  },
  priorityButton: {
    backgroundColor: 'transparent',
    border: '1px solid #bebebe',
    borderRadius: '19px',
    padding: '8px 16px',
    fontSize: '15px',
    color: '#bebebe',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: '118px',
    textAlign: 'center'
  },
  priorityButtonActive: {
    backgroundColor: 'rgba(145,128,253,0.3)',
    border: '1px solid #9180fd',
    color: '#9180fd',
    borderRadius: '5px'
  },
  actionButton: {
    backgroundColor: 'transparent',
    border: 'none',
    padding: '5px 10px',
    cursor: 'pointer',
    marginRight: '15px',
    transition: 'opacity 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  actionIcon: {
    width: '15px',
    height: '15px',
    display: 'block'
  },
  actionButtonHover: {
    color: '#7a6cfd'
  },
  deleteButton: {
    color: '#dc3545'
  },
  loadMoreButton: {
    backgroundColor: '#9180fd',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    color: 'white',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    marginTop: '20px'
  },
  loadMoreButtonHover: {
    backgroundColor: '#7a6cfd'
  },
  modalHeader: {
    backgroundColor: '#f8f8f8',
    borderBottom: '1px solid #bebebe'
  },
  modalTitle: {
    color: '#101828',
    fontWeight: 'bold'
  },
  formControl: {
    border: '1px solid #bebebe',
    borderRadius: '8px',
    padding: '10px 15px',
    fontSize: '14px'
  },
  formLabel: {
    color: '#565656',
    fontWeight: '500',
    marginBottom: '8px'
  }
};

const DigitalVersion2 = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastDocDemo, setLastDocDemo] = useState(null);
  const [lastDocProduction, setLastDocProduction] = useState(null);
  const [hasMoreDemo, setHasMoreDemo] = useState(true);
  const [hasMoreProduction, setHasMoreProduction] = useState(true);
  const [activeTab, setActiveTab] = useState('demo');
  const navigate = useNavigate();
  
  // Add new state variables for the new features
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [mergeOption, setMergeOption] = useState('new');
  const [newSessionName, setNewSessionName] = useState('');
  const [mergeError, setMergeError] = useState('');
  const [mergeSuccess, setMergeSuccess] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  
  useEffect(() => {
    fetchSessions();
  }, [activeTab]);

  const fetchSessions = async (nextPage = false) => {
    setLoading(true);
    try {
      const sessionsRef = collection(db, "digital-sessions");
      const isDemo = activeTab === 'demo';
      const lastDoc = isDemo ? lastDocDemo : lastDocProduction;
      
      let q;
      
      // Query without date filtering to ensure we get everything
      if (nextPage && lastDoc) {
        q = query(
          sessionsRef,
          orderBy("__name__"), // Order by document ID to paginate
          startAfter(lastDoc),
          limit(100) // Fetch more to ensure we have enough after filtering
        );
      } else {
        q = query(
          sessionsRef,
          orderBy("__name__"), // Order by document ID to paginate
          limit(100) // Fetch more to ensure we have enough after filtering
        );
      }
      
      const querySnapshot = await getDocs(q);
      let allSessions = querySnapshot.docs.map(doc => {
        const data = doc.data();
        // Ensure consistent date handling
        let createdAtDate;
        try {
          if (data.createdAt instanceof Timestamp) {
            createdAtDate = data.createdAt.toDate();
          } else if (data.createdAt) {
            createdAtDate = new Date(data.createdAt);
          } else {
            createdAtDate = new Date(0); // Default to epoch start if no date
          }
        } catch (e) {
          console.error("Date parsing error:", e);
          createdAtDate = new Date(0);
        }
        
        return {
          id: doc.id,
          ...data,
          demo: data.demo ?? true,
          createdAt: createdAtDate,
          // Store the original document for pagination purposes
          _originalDoc: doc
        };
      });
      
      // Filter by demo status
      const filteredSessions = allSessions.filter(session => session.demo === isDemo);
      
      // Sort by date, newest first
      filteredSessions.sort((a, b) => b.createdAt - a.createdAt);
      
      // Limit to 30 sessions for display
      const newSessions = filteredSessions.slice(0, 30);
      
      if (filteredSessions.length > 0) {
        if (isDemo) {
          // Use the last document for pagination
          const lastSession = filteredSessions[filteredSessions.length - 1];
          setLastDocDemo(lastSession._originalDoc);
          setHasMoreDemo(filteredSessions.length >= 30);
        } else {
          const lastSession = filteredSessions[filteredSessions.length - 1];
          setLastDocProduction(lastSession._originalDoc);
          setHasMoreProduction(filteredSessions.length >= 30);
        }
      } else {
        if (isDemo) {
          setHasMoreDemo(false);
        } else {
          setHasMoreProduction(false);
        }
      }
      
      // Clean up sessions before setting state (remove _originalDoc)
      const cleanSessions = newSessions.map(({ _originalDoc, ...rest }) => rest);
      
      setSessions(prevSessions => nextPage ? [...prevSessions, ...cleanSessions] : cleanSessions);
    } catch (error) {
      console.error("Error fetching digital violation sessions:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Unknown";
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return (
      <div>
        <div>{`${day}/${month}/${year}`}</div>
        <div>{`${hours}:${minutes}:${seconds}`}</div>
      </div>
    );
  };

  const handleViewClick = async (sessionId) => {
    try {
      const sessionRef = collection(db, `digital-sessions/${sessionId}/userJourneys`);
      const sessionSnapshot = await getDocs(sessionRef);
      const userJourneys = sessionSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      for (const journey of userJourneys) {
        const imagesRef = collection(db, `digital-sessions/${sessionId}/userJourneys/${journey.id}/images`);
        const imagesSnapshot = await getDocs(imagesRef);
        journey.images = imagesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }
      
      navigate("/digital-editor", { state: { sessionId, userJourneys } });
    } catch (error) {
      console.error("Error fetching digital violation session details:", error);
    }
  };

  const toggleSessionType = async (sessionId, currentDemo) => {
    try {
      const sessionRef = doc(db, "digital-sessions", sessionId);
      await updateDoc(sessionRef, {
        demo: !currentDemo
      });
      fetchSessions();
    } catch (error) {
      console.error("Error updating session type:", error);
    }
  };

  /**
   * Handles deleting a session after confirmation
   * @param {string} sessionId - The ID of the session to delete
   */
  const handleDeleteConfirm = async () => {
    if (!sessionToDelete) return;
    
    setIsUpdating(true);
    try {
      // Try to remove from Airtable but don't let it block deletion
      try {
        // Initialize Airtable base
        const base = new Airtable({ apiKey: AIRTABLE_API_KEY }).base(AIRTABLE_BASE_ID);
        
        // Find records for this session
        const records = await base(AIRTABLE_TABLE_ID).select({
          filterByFormula: `{${AIRTABLE_SESSION_FIELD}} = "${sessionToDelete}"`
        }).all();
        
        if (records.length > 0) {
          const recordIds = records.map(record => record.id);
          await base(AIRTABLE_TABLE_ID).destroy(recordIds);
        }
      } catch (airtableError) {
        console.warn(`Failed to update Airtable for session deletion ${sessionToDelete}, but continuing with Firestore deletion:`, airtableError);
      }
      
      // Delete the session document from Firestore (this is the critical operation)
      const sessionRef = doc(db, "digital-sessions", sessionToDelete);
      await deleteDoc(sessionRef);
      
      // Update the sessions list
      setSessions(prevSessions => prevSessions.filter(session => session.id !== sessionToDelete));
      setShowDeleteModal(false);
      setSessionToDelete(null);
    } catch (error) {
      console.error("Error deleting session:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  /**
   * Opens delete confirmation modal for a session
   * @param {string} sessionId - The ID of the session to delete
   */
  const handleDeleteClick = (sessionId) => {
    setSessionToDelete(sessionId);
    setShowDeleteModal(true);
  };

  /**
   * Toggles the priority status of a session
   * @param {string} sessionId - The ID of the session
   * @param {boolean} currentPriority - Current priority status
   */
  const toggleSessionPriority = async (sessionId, currentPriority) => {
    setIsUpdating(true);
    try {
      // Update the priority flag in Firestore first (this is the critical operation)
      const sessionRef = doc(db, "digital-sessions", sessionId);
      const sessionSnapshot = await getDoc(sessionRef);
      const sessionData = sessionSnapshot.data();
      const sessionName = sessionData.sessionName || "Unnamed Session";
      
      const newPriority = !currentPriority;
      await updateDoc(sessionRef, {
        priority: newPriority
      });
      
      // Update local state immediately for better UX
      setSessions(prevSessions => 
        prevSessions.map(session => 
          session.id === sessionId 
            ? {...session, priority: newPriority} 
            : session
        )
      );
      
      // Try to update Airtable but don't let it block the UI
      try {
        // Update Airtable based on the new priority status
        if (newPriority) {
          // Initialize Airtable base
          const base = new Airtable({ apiKey: AIRTABLE_API_KEY }).base(AIRTABLE_BASE_ID);
          
          // Check if session exists in Airtable to avoid duplicates
          const records = await base(AIRTABLE_TABLE_ID).select({
            filterByFormula: `{${AIRTABLE_SESSION_FIELD}} = "${sessionId}"`
          }).all();
          
          if (records.length === 0) {
            // Create entry with session ID in one field and name in another
            await base(AIRTABLE_TABLE_ID).create([
              {
                fields: {
                  [AIRTABLE_SESSION_FIELD]: sessionId,
                  "Session Name": sessionName
                }
              }
            ]);
          } else {
            // Update the existing record with the session name
            await base(AIRTABLE_TABLE_ID).update([
              {
                id: records[0].id,
                fields: {
                  "Session Name": sessionName
                }
              }
            ]);
          }
        } else {
          // Initialize Airtable base
          const base = new Airtable({ apiKey: AIRTABLE_API_KEY }).base(AIRTABLE_BASE_ID);
          
          // Find records for this session
          const records = await base(AIRTABLE_TABLE_ID).select({
            filterByFormula: `{${AIRTABLE_SESSION_FIELD}} = "${sessionId}"`
          }).all();
          
          if (records.length > 0) {
            const recordIds = records.map(record => record.id);
            await base(AIRTABLE_TABLE_ID).destroy(recordIds);
          }
        }
      } catch (airtableError) {
        // Log the Airtable error but don't block UI updates
        console.warn(`Failed to update Airtable for session ${sessionId}, but Firestore was updated:`, airtableError);
      }
      
    } catch (error) {
      console.error("Error updating session priority:", error);
      // Revert local state change if Firestore update failed
      setSessions(prevSessions => 
        prevSessions.map(session => 
          session.id === sessionId 
            ? {...session, priority: currentPriority} 
            : session
        )
      );
    } finally {
      setIsUpdating(false);
    }
  };

  /**
   * Opens merge modal and initializes with selected session
   * @param {string} sessionId - Initially selected session ID
   */
  const handleMergeClick = (sessionId) => {
    setSelectedSessions([sessionId]);
    setShowMergeModal(true);
    setMergeError('');
    setMergeSuccess('');
    setNewSessionName('');
  };

  /**
   * Handles session selection for merging
   * @param {string} sessionId - The session ID to toggle selection
   */
  const toggleSessionSelection = (sessionId) => {
    setSelectedSessions(prev => 
      prev.includes(sessionId)
        ? prev.filter(id => id !== sessionId)
        : [...prev, sessionId]
    );
  };

  /**
   * Performs session merge operation
   */
  const handleMergeConfirm = async () => {
    if (selectedSessions.length < 2) {
      setMergeError('Please select at least two sessions to merge');
      return;
    }
    
    if (mergeOption === 'new' && !newSessionName.trim()) {
      setMergeError('Please enter a name for the new session');
      return;
    }
    
    setIsUpdating(true);
    setMergeError('');
    
    try {
      // Gather session data for merging
      const sessionsData = [];
      for (const sessionId of selectedSessions) {
        const sessionDocRef = doc(db, "digital-sessions", sessionId);
        const sessionDocSnap = await getDoc(sessionDocRef);
        if (sessionDocSnap.exists()) {
          sessionsData.push({
            id: sessionId,
            data: sessionDocSnap.data()
          });
        }
      }
      
      let targetSessionId;
      let targetSessionName;
      
      if (mergeOption === 'new') {
        // Create new session document
        const newSessionRef = doc(collection(db, "digital-sessions"));
        targetSessionId = newSessionRef.id;
        targetSessionName = newSessionName;
        
        // Create the merged session data (set demo to true by default)
        const mergedSessionData = {
          sessionName: newSessionName,
          createdAt: Timestamp.now(),
          createdBy: sessionsData[0].data.createdBy || "System",
          demo: true, // Always set demo to true for new sessions
          mergedFrom: selectedSessions,
          totalUserJourneys: 0 // This will be updated after copying journeys
        };
        
        // Set the new session document
        await setDoc(newSessionRef, mergedSessionData);
      } else {
        // Use the first selected session as the target
        targetSessionId = selectedSessions[0];
        const targetSessionRef = doc(db, "digital-sessions", targetSessionId);
        const targetSessionSnap = await getDoc(targetSessionRef);
        targetSessionName = targetSessionSnap.data().sessionName;
        
        // Update the target session to mark it as receiving merged data
        await updateDoc(targetSessionRef, {
          mergedFrom: selectedSessions.filter(id => id !== targetSessionId),
          lastUpdated: Timestamp.now()
        });
      }
      
      // Copy user journeys to the target session
      let totalJourneys = 0;
      const targetJourneysRef = collection(db, `digital-sessions/${targetSessionId}/userJourneys`);
      const existingJourneysSnap = await getDocs(targetJourneysRef);
      totalJourneys = existingJourneysSnap.size; // Count existing journeys
      
      // If merging into existing session, skip the first session (target)
      const sessionsToMerge = mergeOption === 'new' 
        ? sessionsData.filter(session => session.id !== targetSessionId) 
        : sessionsData;
      
      for (const session of sessionsToMerge) {
        const journeysRef = collection(db, `digital-sessions/${session.id}/userJourneys`);
        const journeysSnapshot = await getDocs(journeysRef);
        
        for (const journeyDoc of journeysSnapshot.docs) {
          // Copy journey to target session
          const newJourneyRef = doc(collection(db, `digital-sessions/${targetSessionId}/userJourneys`));
          await setDoc(newJourneyRef, journeyDoc.data());
          
          // Copy images for this journey
          const imagesRef = collection(db, `digital-sessions/${session.id}/userJourneys/${journeyDoc.id}/images`);
          const imagesSnapshot = await getDocs(imagesRef);
          
          for (const imageDoc of imagesSnapshot.docs) {
            const newImageRef = doc(collection(db, `digital-sessions/${targetSessionId}/userJourneys/${newJourneyRef.id}/images`));
            await setDoc(newImageRef, imageDoc.data());
          }
          
          totalJourneys++;
        }
        
        // Mark merged source sessions (except the target when merging into existing)
        if (mergeOption === 'new' || (mergeOption === 'existing' && session.id !== targetSessionId)) {
          await updateDoc(doc(db, "digital-sessions", session.id), {
            mergedTo: targetSessionId,
            mergedAt: Timestamp.now()
          });
        }
      }
      
      // Update total journey count
      await updateDoc(doc(db, "digital-sessions", targetSessionId), { 
        totalUserJourneys: totalJourneys 
      });
      
      // Try to update Airtable but don't let it block the merging operation
      try {
        // Check which sessions have priority flag in Firebase
        const prioritySessionIds = [];
        for (const sessionId of selectedSessions) {
          // Skip target session if merging into existing
          if (mergeOption === 'existing' && sessionId === targetSessionId) continue;
          
          // Check if session has priority flag
          const sessionDoc = await getDoc(doc(db, "digital-sessions", sessionId));
          if (sessionDoc.exists() && sessionDoc.data().priority) {
            prioritySessionIds.push(sessionId);
          }
        }
        
        // Initialize Airtable
        const base = new Airtable({ apiKey: AIRTABLE_API_KEY }).base(AIRTABLE_BASE_ID);
        
        // Remove merged sessions from Airtable
        for (const sessionId of prioritySessionIds) {
          const records = await base(AIRTABLE_TABLE_ID).select({
            filterByFormula: `{${AIRTABLE_SESSION_FIELD}} = "${sessionId}"`
          }).all();
          
          if (records.length > 0) {
            const recordIds = records.map(record => record.id);
            await base(AIRTABLE_TABLE_ID).destroy(recordIds);
          }
        }
        
        // If any session was priority and we're creating a new session, make the new one priority too
        if (prioritySessionIds.length > 0) {
          if (mergeOption === 'new' || 
              (mergeOption === 'existing' && !prioritySessionIds.includes(targetSessionId))) {
            
            // Get target session data to include name
            const targetSessionRef = doc(db, "digital-sessions", targetSessionId);
            const targetSessionSnap = await getDoc(targetSessionRef);
            const targetSessionData = targetSessionSnap.data();
            const targetSessionName = targetSessionData.sessionName || "Merged Session";
            
            // Update priority flag in Firebase
            await updateDoc(targetSessionRef, { 
              priority: true 
            });
            
            // Add to Airtable with session ID in one field and name in another
            await base(AIRTABLE_TABLE_ID).create([{
              fields: {
                [AIRTABLE_SESSION_FIELD]: targetSessionId,
                "Session Name": targetSessionName
              }
            }]);
          }
        }
      } catch (airtableError) {
        console.warn(`Failed to update Airtable for merged sessions, but continuing with merge operation:`, airtableError);
      }
      
      setMergeSuccess(`Sessions merged successfully into "${targetSessionName}"`);
      fetchSessions(); // Refresh the sessions list
      
      // Delay closing the modal to allow user to see success message
      setTimeout(() => {
        setShowMergeModal(false);
        setSelectedSessions([]);
      }, 2000);
      
    } catch (error) {
      console.error("Error merging sessions:", error);
      setMergeError(`Failed to merge sessions: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div style={customStyles.container}>
      {/* Header Section */}
      <div style={customStyles.header}>
        <div style={customStyles.headerIcon}>
          <img src={imgSessionsIcon} alt="Sessions" style={{ width: '100%', height: '100%' }} />
        </div>
        <h1 style={customStyles.headerTitle}>Digital Violation Sessions Overview</h1>
      </div>

      {/* Custom Tabs */}
      <div style={customStyles.tabsContainer}>
        <div style={{ display: 'flex', gap: '20px', marginBottom: '30px' }}>
          <button
            style={{
              ...customStyles.tabButton,
              ...(activeTab === 'demo' ? customStyles.tabButtonActive : {})
            }}
            onClick={() => setActiveTab('demo')}
          >
            Demo sessions
          </button>
          <button
            style={{
              ...customStyles.tabButton,
              ...(activeTab === 'production' ? customStyles.tabButtonActive : {})
            }}
            onClick={() => setActiveTab('production')}
          >
            Production sessions
          </button>
        </div>
      </div>

      {/* Sessions Table */}
      {renderSessionsTable()}
      
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton style={customStyles.modalHeader}>
          <Modal.Title style={customStyles.modalTitle}>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this session? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteConfirm} 
            disabled={isUpdating}
          >
            {isUpdating ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Merge Sessions Modal */}
      <Modal show={showMergeModal} onHide={() => setShowMergeModal(false)} size="lg">
        <Modal.Header closeButton style={customStyles.modalHeader}>
          <Modal.Title style={customStyles.modalTitle}>Merge Sessions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mergeError && <Alert variant="danger">{mergeError}</Alert>}
          {mergeSuccess && <Alert variant="success">{mergeSuccess}</Alert>}
          
          <p>Select the sessions you want to merge:</p>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Select</th>
                  <th>Session Name</th>
                  <th>Created By</th>
                  <th>Created At</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(session => (
                  <tr key={session.id}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedSessions.includes(session.id)}
                        onChange={() => toggleSessionSelection(session.id)}
                      />
                    </td>
                    <td>{session.sessionName}</td>
                    <td>{session.createdBy}</td>
                    <td>{formatTimestamp(session.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          
          <Form className="mt-3">
            <Form.Group>
              <Form.Label style={customStyles.formLabel}>Merge Option</Form.Label>
              <Form.Select 
                value={mergeOption}
                onChange={(e) => setMergeOption(e.target.value)}
                style={customStyles.formControl}
              >
                <option value="new">Create a new session</option>
                <option value="existing">Merge into selected session</option>
              </Form.Select>
            </Form.Group>
            
            {mergeOption === 'new' && (
              <Form.Group className="mt-2">
                <Form.Label style={customStyles.formLabel}>New Session Name</Form.Label>
                <Form.Control
                  type="text"
                  value={newSessionName}
                  onChange={(e) => setNewSessionName(e.target.value)}
                  placeholder="Enter new session name"
                  style={customStyles.formControl}
                />
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMergeModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleMergeConfirm}
            disabled={isUpdating || selectedSessions.length < 2}
          >
            {isUpdating ? "Merging..." : "Merge Sessions"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );

  function renderSessionsTable() {
    const isDemo = activeTab === 'demo';
    const hasMore = isDemo ? hasMoreDemo : hasMoreProduction;
    
    return (
      <>
        {loading && sessions.length === 0 ? (
          <p>Loading sessions...</p>
        ) : (
          <div style={customStyles.tableContainer}>
            <Table style={{ margin: 0 }}>
              <thead style={customStyles.tableHeader}>
                <tr>
                  <th style={customStyles.tableHeaderCell}>SESSION NAME</th>
                  <th style={customStyles.tableHeaderCell}>CREATED BY</th>
                  <th style={customStyles.tableHeaderCell}>CREATED AT</th>
                  <th style={customStyles.tableHeaderCell}>PRIORITY</th>
                  <th style={customStyles.tableHeaderCell}>ACTIONS</th>
                </tr>
              </thead>
              <tbody style={customStyles.tableBody}>
                {sessions.length > 0 ? (
                  sessions.map(session => (
                    <tr key={session.id} style={customStyles.tableRow}>
                      <td style={customStyles.tableCell}>
                        <div style={customStyles.sessionName}>
                          {session.sessionName}
                        </div>
                        <div style={customStyles.sessionDescription}>
                          Add description
                        </div>
                      </td>
                      <td style={customStyles.tableCell}>{session.createdBy}</td>
                      <td style={customStyles.tableCell}>
                        {formatTimestamp(session.createdAt)}
                      </td>
                      <td style={customStyles.tableCell}>
                        <button
                          style={{
                            ...customStyles.priorityButton,
                            ...(session.priority ? customStyles.priorityButtonActive : {})
                          }}
                          onClick={() => toggleSessionPriority(session.id, session.priority)}
                          disabled={isUpdating}
                        >
                          {session.priority ? "Priority" : "Set priority"}
                        </button>
                      </td>
                      <td style={customStyles.tableCell}>
                        <button
                          style={customStyles.actionButton}
                          onClick={() => handleViewClick(session.id)}
                          title="View Session"
                        >
                          <img src={imgViewIcon} alt="View" style={customStyles.actionIcon} />
                        </button>
                        <button
                          style={customStyles.actionButton}
                          onClick={() => toggleSessionType(session.id, session.demo)}
                          title={`Move to ${session.demo ? 'Production' : 'Demo'}`}
                        >
                          <img src={imgMoveIcon} alt="Move" style={customStyles.actionIcon} />
                        </button>
                        <button
                          style={customStyles.actionButton}
                          onClick={() => handleMergeClick(session.id)}
                          title="Merge Sessions"
                        >
                          <img src={imgMergeIcon} alt="Merge" style={customStyles.actionIcon} />
                        </button>
                        <button
                          style={customStyles.actionButton}
                          onClick={() => handleDeleteClick(session.id)}
                          title="Delete Session"
                        >
                          <img src={imgDeleteIcon} alt="Delete" style={customStyles.actionIcon} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" style={customStyles.tableCell}>No sessions available.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
        {hasMore && (
          <button 
            style={customStyles.loadMoreButton}
            onClick={() => fetchSessions(true)} 
            disabled={loading}
          >
            {loading ? "Loading..." : "Load More"}
          </button>
        )}
      </>
    );
  }
};

export default DigitalVersion2;
